import { normalizeFormElementType } from '@/common/utils/formElementType'

/** Имя listener-события Swing для однострочного Text (не Textarea/Password). */
export const TEXT_EDITED_LISTENER_EVENT = 'textEdited'

export function isPlainTextFieldType(type: string | undefined | null): boolean {
  return String(type ?? '').trim().toUpperCase() === 'TEXT'
}

/**
 * Разрешить invokeListeners для TEXT: onChange (Enter/blur/…) и textEdited (ввод).
 * PASSWORD/TEXTAREA — только onChange, как раньше.
 */
export function shouldAllowTextFieldListenerEvent(
  eventName: string,
  elementType: string | undefined | null,
): boolean {
  const typeNorm = String(elementType ?? '').trim().toUpperCase()
  const isTextLike =
    typeNorm === 'TEXT' || typeNorm === 'PASSWORD' || typeNorm === 'TEXTAREA' || typeNorm === 'MULTILINETEXT'
  if (!isTextLike) return true

  const ev = String(eventName ?? '').trim()
  if (ev === 'onChange' || ev === 'change') return true
  const isTextEdited =
    ev.toLowerCase() === TEXT_EDITED_LISTENER_EVENT.toLowerCase() &&
    isPlainTextFieldType(typeNorm)
  if (isTextEdited) return true
  return false
}

/** textEdited — только при пользовательском вводе в однострочный Text. */
export function shouldFireTextEditedOnInput(
  elementType: string | undefined | null,
  isUserInput: boolean,
): boolean {
  return isPlainTextFieldType(elementType) && isUserInput
}

function extractFormElementId(element: Record<string, unknown>): string | number | undefined {
  const attrs = element._attributes as { id?: string | number } | undefined
  const raw = attrs?.id ?? element.id ?? element.elementId
  if (raw == null || raw === '') return undefined
  const asNumber = Number(raw)
  return Number.isFinite(asNumber) && asNumber > 0 ? asNumber : String(raw)
}

function walkFormDefinitionByElementId(
  node: unknown,
  visit: (element: Record<string, unknown>) => void,
): void {
  if (node == null) return
  if (Array.isArray(node)) {
    node.forEach((item) => walkFormDefinitionByElementId(item, visit))
    return
  }
  if (typeof node !== 'object') return

  const record = node as Record<string, unknown>
  const element = record.element
  if (element && typeof element === 'object') {
    visit(element as Record<string, unknown>)
  }

  for (const key of ['rows', 'main', 'header', 'footer', 'tr', 'td', 'panelRows', 'panels']) {
    if (record[key] != null) {
      walkFormDefinitionByElementId(record[key], visit)
    }
  }
}

/** Тип элемента формы по numeric id (onLoad до mount registry). */
export function resolveFormElementTypeById(
  formRoot: unknown,
  elementId: number | string,
): string | undefined {
  const wanted = String(elementId ?? '').trim()
  if (!formRoot || typeof formRoot !== 'object' || !wanted) return undefined

  let hit: string | undefined
  const visit = (element: Record<string, unknown>) => {
    if (hit) return
    const id = extractFormElementId(element)
    if (id == null || String(id) !== wanted) return
    hit = normalizeFormElementType(element)
  }

  const root = formRoot as Record<string, unknown>
  const panels = root.panels
  if (panels && typeof panels === 'object' && !Array.isArray(panels)) {
    for (const panel of Object.values(panels as Record<string, unknown>)) {
      walkFormDefinitionByElementId(panel, visit)
    }
  }

  walkFormDefinitionByElementId(root.main, visit)
  walkFormDefinitionByElementId(root.header, visit)
  walkFormDefinitionByElementId(root.footer, visit)
  if (!panels) {
    walkFormDefinitionByElementId(root, visit)
  }

  return hit
}
